import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import cn from 'classnames';
import { snakeCase } from 'lodash';

import { ModalModel, TextAlign, ModalContext } from '@notino/react-styleguide';
import {
  AvailabilityState,
  CartServiceType,
  GetCatalogProductViewQuery,
} from '@notino/shared/definitions/types';
import { AdditionalServicesAvailability, CartType } from '@notino/web-tracking';
import { Ga4Events } from '@notino/web-tracking/dist/types/package/ga4/events';

import ErrorBoundary from '@components/ErrorBoundary';
import { useTrackingDisplayType } from '@containers/ProductDetailContainer/utils';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';
import { AddToCartButton } from '@sharedComponents/AddToCartButton/AddToCartButton';

import { ICurrency } from '../../../../App/model';
import { usePriceLogic } from '../../hooks/usePriceLogic';
import {
  MIN_PRICE_PROMO_LABEL,
  RRP_PRICE_PROMO_LABEL,
} from '../../hooks/usePriceLogic/tracking';
import { trackPageReload } from '../../tracking';
import { ClickAndCollect } from '../ClickAndCollect';
import { useEngravingTryItFirstContext } from '../context/EngravingTryItFirst';
import { getRenderableDiscoBoxSampleId } from '../DiscoveryBoxSelfChoice';
import { useIsSkinAnalyzerVisible } from '../SkinAnalyser/hooks/useIsSkinAnalyzerVisible';
import { useResolvedVoucherDiscount } from '../VoucherDiscount/useResolvedVoucherDiscount';

import { messages } from './addToCartMessages';
import { useStickyAtMainBottom } from './hooks/useStickyAtMainBottom';
import {
  ButtonWrapper,
  BuyButtons,
  Label,
  DropdownButtonSeparator,
  QuantityWrapper,
  AddToCartWrapper,
  ClickAndCollectWrapper,
  StyledQuantitySelector,
  clickAndCollectGridTemplateAread,
  StickyPlaceholder,
} from './styled';
import { trackEngravingAddToCart } from './tracking';
import { prepareOptions } from './utils/prepareOptions';

interface IAddToCartProps {
  product: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >;
  variant: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >['variants'][number];
  orderUnit: string;
  productCode: string;
  currency: ICurrency;
  onProductAdded: () => void;
  engravingAvailable: AdditionalServicesAvailability;
  tryItFirstAvailable: AdditionalServicesAvailability;
  quantity: number;
  setQuantity: (quantity: number) => void;
}

const QUANTITY_SELECTOR_KEYBOARD_CONFIG = {
  keyNavigation: true,
};

export const AddToCart: React.FC<IAddToCartProps> = React.memo(
  ({
    variant,
    product,
    quantity,
    orderUnit,
    setQuantity,
    onProductAdded,
    engravingAvailable,
    tryItFirstAvailable,
  }) => {
    const {
      state: { engravingInitials, withEngraving, withTryItFirst },
    } = useEngravingTryItFirstContext();
    const { hideModal, toggleModal } = ModalContext.useModalContext();
    const { getTimeFromInit } = useTrackingContext();
    const { formatMessage } = useIntl();
    const { rrpShown, minimalPriceShown } = usePriceLogic(variant);
    const featureFlags = useFeatureFlags();
    const hasVoucher = !!useResolvedVoucherDiscount();

    const shouldShowSkinAnalyzer = useIsSkinAnalyzerVisible();

    const trackingDisplayType = useTrackingDisplayType(product);

    const { wrapperRef, sentinelRef } = useStickyAtMainBottom();

    const onModalClose = React.useCallback(() => {
      setQuantity(1);
      hideModal();
    }, [hideModal, setQuantity]);

    const changeAmount = React.useCallback(
      (newQuantity: number): void => {
        if (withEngraving && newQuantity > 1) {
          const modalOptions = {
            header: (
              <div data-cypress="engraving-maxOrderQuantity">
                {formatMessage(messages.engravingMaxOrderQtyHeader, {
                  quantity: 1,
                  unit: orderUnit,
                })}
              </div>
            ),
            type: ModalModel.ModalTypes.default,
            onClose: onModalClose,
          };

          const modalContent = (
            <FormattedMessage {...messages.engravingMaxOrderQtyDesc} />
          );

          toggleModal(modalContent, modalOptions);
        }
        setQuantity(newQuantity);
      },
      [
        formatMessage,
        onModalClose,
        orderUnit,
        toggleModal,
        withEngraving,
        setQuantity,
      ]
    );

    const handleGtmPushProductDetail = React.useCallback(() => {
      trackPageReload({
        rrpShown,
        minimalPriceShown,
        variant,
        product,
        engravingAvailable,
        tryItFirstAvailable,
        trackingDisplayType,
        featureFlags,
        skinAnalyzer: shouldShowSkinAnalyzer,
        hasVoucher,
      });
    }, [
      variant,
      product,
      engravingAvailable,
      tryItFirstAvailable,
      rrpShown,
      minimalPriceShown,
      trackingDisplayType,
      featureFlags,
      shouldShowSkinAnalyzer,
      hasVoucher,
    ]);

    const handleQuantitySelectorClick = () => {
      dispatchTrackingEvent({
        event: 'element_click',
        element: {
          timing: getTimeFromInit(),
          interaction: 'click',
          mode: undefined,
          name: 'quantity_selector',
          type: 'product_detail',
          action: 'click_on_element',
          promo_labels: undefined,
        },
        _clear: true,
      });
    };

    const services = React.useMemo(
      () =>
        withEngraving && [
          {
            type: CartServiceType.Engraving,
            value: engravingInitials,
            count: quantity,
            productId: variant.webId,
          },
        ],
      [withEngraving, engravingInitials, quantity, variant.webId]
    );

    const trackingProduct: Extract<
      Ga4Events,
      { event: 'add_to_cart' }
    >['product'] = React.useMemo(
      () =>
        ProductEventWither()
          .withProduct(product)
          .withVariant(variant)
          .withDiscountPrices(variant.attributes)
          .withServices({
            tryItFirstAvailable,
            engravingAvailable,
            skinAnalyzer: shouldShowSkinAnalyzer,
            discoveryBoxAvailable: Boolean(
              getRenderableDiscoBoxSampleId(
                product,
                variant,
                featureFlags as Pick<
                  typeof featureFlags,
                  'discovery_box_self_choice_enabled'
                >
              )
            ),
          })
          .withAdditionalData({ quantity, cart_type: snakeCase(CartType.cart) })
          .withAdditionalPromoLabels(
            [
              rrpShown && RRP_PRICE_PROMO_LABEL,
              minimalPriceShown && MIN_PRICE_PROMO_LABEL,
            ].filter(Boolean) as string[]
          )
          .build(),
      [
        engravingAvailable,
        tryItFirstAvailable,
        product,
        minimalPriceShown,
        rrpShown,
        quantity,
        variant,
        featureFlags,
        shouldShowSkinAnalyzer,
      ]
    );

    const handleProductAdded = React.useCallback(() => {
      onProductAdded();

      const additionalPromoLabels = [
        rrpShown && RRP_PRICE_PROMO_LABEL,
        minimalPriceShown && MIN_PRICE_PROMO_LABEL,
      ].filter((v): v is string => typeof v === 'string');
      dispatchTrackingEvent({
        event: 'add_to_cart',
        product: trackingProduct,
        _clear: true,
      });

      if (withEngraving) {
        trackEngravingAddToCart(
          product,
          variant,
          quantity,
          additionalPromoLabels
        );
      }
    }, [
      onProductAdded,
      rrpShown,
      minimalPriceShown,
      trackingProduct,
      withEngraving,
      product,
      variant,
      quantity,
    ]);

    const trackAddFailed = React.useCallback(
      (message: string) => {
        dispatchTrackingEvent({
          event: 'add_to_cart_failed',
          add: {
            products: [trackingProduct],
          },
          error: {
            status: message || '',
          },
          _clear: true,
        });
      },
      [trackingProduct]
    );

    const canBuy = variant.availability.state === AvailabilityState.CanBeBought;
    const isClickAndCollect = variant.availability.availablePickUpStores > 0;

    if (!canBuy && !isClickAndCollect) {
      return null;
    }

    return (
      <ErrorBoundary>
        <BuyButtons
          id="pdAddToCart"
          data-testid="pdAddToCart"
          className={cn({
            [clickAndCollectGridTemplateAread]: isClickAndCollect,
          })}
        >
          {canBuy && (
            <AddToCartWrapper
              isClickAndCollect={isClickAndCollect}
              ref={wrapperRef}
            >
              <Label>
                <FormattedMessage {...messages.addToCartQuantity} />
              </Label>
              <>
                <QuantityWrapper onClick={handleQuantitySelectorClick}>
                  <StyledQuantitySelector
                    options={prepareOptions(
                      variant.availability.maxOrderQuantity
                    )}
                    disableSelectWhenSingleOption={true}
                    handleOptionSelect={changeAmount}
                    currentOption={quantity}
                    textAlign={TextAlign.center}
                    keyNavigationConfig={QUANTITY_SELECTOR_KEYBOARD_CONFIG}
                    selectDataTestId="selected-value-quantity"
                  />
                </QuantityWrapper>
                <DropdownButtonSeparator />
              </>

              <AddToCartButton
                product={{
                  ...variant,
                  id: variant.webId,
                  masterId: product.webMasterId,
                  name: variant.name ?? '',
                  variantName: variant.variantName ?? '',
                }}
                count={quantity}
                services={services || undefined}
                buttonWrapper={ButtonWrapper}
                showAddToCartModal={product.config.showAddToCartModal ?? false}
                buttonElementId="pd-buy-button"
                onProductAdded={handleProductAdded}
                onProductAddFailed={trackAddFailed}
                onClosingModal={handleGtmPushProductDetail}
                withLegacyAddToCart={true}
              />
            </AddToCartWrapper>
          )}
          <StickyPlaceholder ref={sentinelRef} />
          {isClickAndCollect && (
            <ClickAndCollectWrapper isClickAndCollect={isClickAndCollect}>
              <ClickAndCollect
                selectedVariant={variant}
                product={product}
                onModalClosing={handleGtmPushProductDetail}
                disabled={withEngraving || withTryItFirst || quantity > 1}
              />
            </ClickAndCollectWrapper>
          )}
        </BuyButtons>
      </ErrorBoundary>
    );
  }
);

AddToCart.displayName = 'AddToCart';
